import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Avatar, Box, Center, HStack, Spinner, Table, Text } from '@chakra-ui/react';
import { PaginationItems, PaginationNextTrigger, PaginationPrevTrigger, PaginationRoot } from '@src/components/chakra-ui/pagination';
import { ButtonLink } from '@src/components/sco/ButtonLink.tsx';
import { ToolSessionSummary } from '@src/components/sco/Tools/ToolSessionSummary.tsx';
import { ScoToolSession } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser.ts';
import { gql } from '@src/lib/gql.ts';
import { DateTime } from 'luxon';
import React from 'react';
import { FiEye } from 'react-icons/fi';
const PAGE_SIZE = 10;
export type ToolSessionsTableProps = {
  toolId?: string;
};
export const ToolSessionsTable: React.FC<ToolSessionsTableProps> = props => {
  const $ = _c(52);
  const [page, setPage] = React.useState(1);
  const {
    currentUser
  } = useCurrentUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($toolId: String, $userId: String, $order: [[String]], $page: Int, $pageSize: Int) {
        scoToolSessions(toolId: $toolId, userId: $userId, order: $order, page: $page, pageSize: $pageSize) {
          id
          items {
            id
            toolSessionId
            title
            tool {
              id
              title
              iconUrl
              toolId
              slug
            }
            createdAt
            lastActiveAt
            thread {
              id
              threadId
              lastActiveAt
              commentsCount
            }
          }
          pagination {
            hasNextPage
            itemCount
            page
            pageSize
          }
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = currentUser?.userId;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [["createdAt", "desc"]];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== page || $[3] !== props.toolId || $[4] !== t1) {
    t3 = {
      userId: t1,
      toolId: props.toolId,
      order: t2,
      page,
      pageSize: PAGE_SIZE
    };
    $[2] = page;
    $[3] = props.toolId;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !currentUser?.userId;
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      variables: t3,
      skip: t4,
      fetchPolicy: "cache-and-network"
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const query = useQuery(t0, t5);
  let T0;
  let T1;
  let T2;
  let T3;
  let itemCount;
  let sessions;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== query.data?.scoToolSessions?.items || $[10] !== query.data?.scoToolSessions?.pagination?.itemCount || $[11] !== query.error) {
    sessions = (query.data?.scoToolSessions?.items ?? []).filter(Boolean) as ScoToolSession[];
    itemCount = query.data?.scoToolSessions?.pagination?.itemCount ?? 0;
    T3 = Box;
    if ($[22] !== query.error) {
      t9 = query.error && <Box color="red.500" mb={4}>{"Error loading sessions: "}{query.error.message}</Box>;
      $[22] = query.error;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    T2 = Table.ScrollArea;
    T1 = Table.Root;
    t7 = "outline";
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Table.Header><Table.Row><Table.ColumnHeader>Last Active</Table.ColumnHeader><Table.ColumnHeader>Tool</Table.ColumnHeader><Table.ColumnHeader>Summary</Table.ColumnHeader><Table.ColumnHeader>Messages</Table.ColumnHeader><Table.ColumnHeader textAlign="end">Actions</Table.ColumnHeader></Table.Row></Table.Header>;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    T0 = Table.Body;
    t6 = sessions.map(_temp);
    $[9] = query.data?.scoToolSessions?.items;
    $[10] = query.data?.scoToolSessions?.pagination?.itemCount;
    $[11] = query.error;
    $[12] = T0;
    $[13] = T1;
    $[14] = T2;
    $[15] = T3;
    $[16] = itemCount;
    $[17] = sessions;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[12];
    T1 = $[13];
    T2 = $[14];
    T3 = $[15];
    itemCount = $[16];
    sessions = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t10;
  if ($[25] !== query.loading || $[26] !== sessions) {
    t10 = query.loading && sessions.length === 0 && <Table.Row><Table.Cell colSpan={5}><Center py={8}><Spinner /></Center></Table.Cell></Table.Row>;
    $[25] = query.loading;
    $[26] = sessions;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== query.loading || $[29] !== sessions) {
    t11 = !query.loading && sessions.length === 0 && <Table.Row><Table.Cell colSpan={5}><Center color="fg.muted" py={8}>No sessions found.</Center></Table.Cell></Table.Row>;
    $[28] = query.loading;
    $[29] = sessions;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== T0 || $[32] !== t10 || $[33] !== t11 || $[34] !== t6) {
    t12 = <T0>{t6}{t10}{t11}</T0>;
    $[31] = T0;
    $[32] = t10;
    $[33] = t11;
    $[34] = t6;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== T1 || $[37] !== t12 || $[38] !== t7 || $[39] !== t8) {
    t13 = <T1 variant={t7}>{t8}{t12}</T1>;
    $[36] = T1;
    $[37] = t12;
    $[38] = t7;
    $[39] = t8;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== T2 || $[42] !== t13) {
    t14 = <T2>{t13}</T2>;
    $[41] = T2;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== itemCount || $[45] !== page) {
    t15 = itemCount > PAGE_SIZE && <PaginationRoot count={itemCount} mt={4} page={page} pageSize={PAGE_SIZE} onPageChange={e => setPage(e.page)}><HStack justify="end"><PaginationPrevTrigger /><PaginationItems /><PaginationNextTrigger /></HStack></PaginationRoot>;
    $[44] = itemCount;
    $[45] = page;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== T3 || $[48] !== t14 || $[49] !== t15 || $[50] !== t9) {
    t16 = <T3>{t9}{t14}{t15}</T3>;
    $[47] = T3;
    $[48] = t14;
    $[49] = t15;
    $[50] = t9;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  return t16;
};
function _temp(session) {
  const lastActiveAt = session.lastActiveAt || session.thread?.lastActiveAt || session.createdAt;
  const lastActiveDt = lastActiveAt ? DateTime.fromISO(lastActiveAt) : null;
  return <Table.Row key={session.id}><Table.Cell whiteSpace="nowrap">{lastActiveDt?.isValid ? lastActiveDt.toLocaleString(DateTime.DATETIME_MED) : "\u2014"}</Table.Cell><Table.Cell><HStack><Avatar.Root borderRadius="md" size="xs"><Avatar.Fallback borderRadius="md" name={session.tool?.title ?? undefined} /><Avatar.Image borderRadius="md" src={session.tool?.iconUrl ?? undefined} /></Avatar.Root><Text fontWeight="medium">{session.tool?.title ?? "Unknown Tool"}</Text></HStack></Table.Cell><Table.Cell color="fg.muted" maxWidth="md"><ToolSessionSummary textProps={{
        lineClamp: 1
      }} toolSessionId={session.toolSessionId} /></Table.Cell><Table.Cell textAlign="end">{typeof session.thread?.commentsCount === "number" ? session.thread.commentsCount.toLocaleString("en-US") : "\u2014"}</Table.Cell><Table.Cell textAlign="end"><ButtonLink href={`/tools/${session.tool?.slug}/sessions/${session.toolSessionId}`} variant="outline"><FiEye />Open</ButtonLink></Table.Cell></Table.Row>;
}